/**
 * TasksContext - Provides task state management throughout the app
 *
 * Manages:
 * - Active task tracking
 * - Task list loading (API-based)
 * - Real-time async task updates (Firestore onSnapshot)
 * - Task switching with context preservation
 * - Loading/error states
 *
 * Uses the memory-agent Tasks API for persistent task management,
 * and Firestore onSnapshot for real-time task-engine updates.
 */
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuthContext } from './AuthContext';
import {
  Task,
  listTasks,
  getActiveTask,
  switchTask,
  createTask,
  updateTask,
  deleteTask
} from '../services/tasksApi';
import { collection, query, where, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../services/firebase';

// =============================================================================
// TYPES
// =============================================================================

/** Task-engine async task from Firestore `superjarvis_tasks` collection */
export interface AsyncTask {
  task_id: string;
  user_id: string;
  intent: string;
  status: string;
  total_subtasks: number;
  completed_subtasks: number;
  failed_subtasks: number;
  subtasks: Record<string, {
    id: string;
    domain: string;
    description: string;
    status: string;
  }>;
  results: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface TasksContextType {
  // State
  tasks: Task[];
  activeTask: Task | null;
  asyncTasks: AsyncTask[];
  loading: boolean;
  error: string | null;

  // Actions
  refreshTasks: () => Promise<void>;
  switchToTask: (taskId: string) => Promise<boolean>;
  createNewTask: (title: string, description?: string) => Promise<Task | null>;
  updateTaskStatus: (taskId: string, status: Task['status']) => Promise<boolean>;
  deleteTaskById: (taskId: string) => Promise<boolean>;
  clearError: () => void;
}

// =============================================================================
// CONTEXT
// =============================================================================

const TasksContext = createContext<TasksContextType | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

export function TasksProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [asyncTasks, setAsyncTasks] = useState<AsyncTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Refresh the tasks list and active task via API
   */
  const refreshTasks = useCallback(async () => {
    if (!user) {
      setTasks([]);
      setActiveTask(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [tasksResponse, activeResponse] = await Promise.allSettled([
        listTasks({ user_id: user.uid }),
        getActiveTask({ user_id: user.uid }),
      ]);

      if (tasksResponse.status === 'fulfilled' && tasksResponse.value.success) {
        setTasks(tasksResponse.value.tasks || []);
      }
      if (activeResponse.status === 'fulfilled' && activeResponse.value.success) {
        setActiveTask(activeResponse.value.task || null);
      }
    } catch (err) {
      console.error('[TasksContext] Failed to refresh tasks:', err);
      // Don't show error to user — tasks just won't load
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Switch to a different task
   */
  const switchToTask = useCallback(async (taskId: string): Promise<boolean> => {
    if (!user) return false;

    setLoading(true);
    setError(null);

    try {
      const response = await switchTask({
        user_id: user.uid,
        task_id: taskId,
        preserve_context: true
      });

      if (response.success) {
        setActiveTask(response.current_task);
        // Update the task in the list as well
        setTasks(prev => prev.map(t =>
          t.task_id === taskId
            ? { ...t, status: 'running' as const }
            : t.task_id === response.previous_task?.task_id
              ? { ...t, status: 'paused' as const }
              : t
        ));
        return true;
      } else {
        setError(response.message || 'Failed to switch task');
        return false;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to switch task';
      setError(message);
      console.error('[TasksContext] Error switching task:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Create a new task
   */
  const createNewTask = useCallback(async (title: string, description?: string): Promise<Task | null> => {
    if (!user) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await createTask({
        user_id: user.uid,
        title,
        description
      });

      if (response.success && response.task) {
        // Add to list and set as active
        setTasks(prev => [response.task, ...prev]);
        setActiveTask(response.task);
        return response.task;
      } else {
        setError(response.message || 'Failed to create task');
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create task';
      setError(message);
      console.error('[TasksContext] Error creating task:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Update a task's status
   */
  const updateTaskStatus = useCallback(async (taskId: string, status: Task['status']): Promise<boolean> => {
    if (!user) return false;

    // Optimistic update
    const previousTasks = tasks;
    setTasks(prev => prev.map(t =>
      t.task_id === taskId ? { ...t, status, updated_at: new Date().toISOString() } : t
    ));

    try {
      await updateTask({ user_id: user.uid, task_id: taskId, status });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update task';
      setError(message);
      console.error('[TasksContext] Error updating task:', err);
      // Revert on failure
      setTasks(previousTasks);
      return false;
    }
  }, [user, tasks]);

  /**
   * Delete a task
   */
  const deleteTaskById = useCallback(async (taskId: string): Promise<boolean> => {
    if (!user) return false;

    // Optimistic remove
    const previousTasks = tasks;
    setTasks(prev => prev.filter(t => t.task_id !== taskId));

    try {
      await deleteTask(taskId, user.uid);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete task';
      setError(message);
      console.error('[TasksContext] Error deleting task:', err);
      // Revert on failure
      setTasks(previousTasks);
      return false;
    }
  }, [user, tasks]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load tasks when user changes (API-based)
  useEffect(() => {
    if (user) {
      refreshTasks();
    } else {
      setTasks([]);
      setActiveTask(null);
      setAsyncTasks([]);
    }
  }, [user, refreshTasks]);

  // Real-time Firestore listener for task-engine async tasks
  useEffect(() => {
    if (!user?.uid) {
      setAsyncTasks([]);
      return;
    }

    const q = query(
      collection(db, 'superjarvis_tasks'),
      where('user_id', '==', user.uid),
      orderBy('created_at', 'desc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const newAsyncTasks: AsyncTask[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            task_id: doc.id,
            user_id: data.user_id || '',
            intent: data.intent || '',
            status: data.status || 'unknown',
            total_subtasks: data.total_subtasks || 0,
            completed_subtasks: data.completed_subtasks || 0,
            failed_subtasks: data.failed_subtasks || 0,
            subtasks: data.subtasks || {},
            results: data.results || {},
            created_at: data.created_at?.toDate?.()?.toISOString?.() || '',
            updated_at: data.updated_at?.toDate?.()?.toISOString?.() || '',
          };
        });
        setAsyncTasks(newAsyncTasks);
        console.log('[TasksContext] Async tasks updated:', newAsyncTasks.length);
      },
      (err) => {
        // Firestore index might not exist yet — graceful fallback
        console.warn('[TasksContext] Firestore listener error (index may need creation):', err.message);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  return (
    <TasksContext.Provider value={{
      tasks,
      activeTask,
      asyncTasks,
      loading,
      error,
      refreshTasks,
      switchToTask,
      createNewTask,
      updateTaskStatus,
      deleteTaskById,
      clearError
    }}>
      {children}
    </TasksContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useTasks(): TasksContextType {
  const context = useContext(TasksContext);
  if (!context) {
    throw new Error('useTasks must be used within a TasksProvider');
  }
  return context;
}

export default TasksContext;
